import { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, LinearProgress, Button,
  CircularProgress, Alert, Chip, Divider, Link, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import StorageIcon from '@mui/icons-material/Storage';
import { getApiUsage, getSerpapiUsage, getAnthropicUsage } from '../services/api';

/**
 * Hybrid API usage view.
 *
 * Three data sources, shown together:
 *   1. Live SerpAPI Account API       → /api/usage/serpapi
 *   2. Live Anthropic Admin API       → /api/usage/anthropic (optional)
 *   3. Internal counters (Adzuna, Claude, Tailor) → /api/usage
 *
 * Provider API keys live in the backend only — we never call providers from
 * the browser. Admin API key is optional; page degrades gracefully if absent.
 *
 * This page has no Navbar link. Access via URL: /usage
 */

// Internal-counter metadata (matches backend rate_limiter.LIMITS)
const INTERNAL_META = {
  claude: {
    label: 'Claude (internal counter)',
    description: 'Local daily cap on Claude calls (not provider data).',
    dashboard: 'https://console.anthropic.com',
  },
  tailor: {
    label: 'Resume Tailoring',
    description: 'Daily cap on tailor generations. Uses Claude internally.',
    dashboard: null,
  },
  adzuna: {
    label: 'Adzuna',
    description: 'Fallback job search provider. No live endpoint available.',
    dashboard: 'https://developer.adzuna.com/admin/access_details',
  },
};

// Only these internal entries are shown in the "internal counters" card.
// serpapi is hidden here because its live card supersedes it.
const INTERNAL_ORDER = ['claude', 'tailor', 'adzuna'];

// Color ramp: >30% remaining = success, 10–30% = warning, <10% = error
function colorFor(remaining, limit) {
  if (!limit) return 'success';
  const pct = remaining / limit;
  if (pct <= 0.1) return 'error';
  if (pct <= 0.3) return 'warning';
  return 'success';
}

// ─── Reusable row for limit-based usage (SerpAPI + internal counters) ─────
function UsageRow({ label, description, used, limit, remaining, period, resets, dashboard, sourceTag }) {
  const usedPct = limit > 0 ? (used / limit) * 100 : 0;
  const color = colorFor(remaining, limit);
  const isLow = color !== 'success';

  return (
    <Box sx={{ py: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} gap={2}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>{label}</Typography>
            {sourceTag}
          </Box>
          {description && (
            <Typography variant="caption" color="text.secondary">{description}</Typography>
          )}
        </Box>
        <Box textAlign="right" sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" fontWeight={600}>
            {used} / {limit}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {remaining} remaining · resets {resets}
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={Math.min(usedPct, 100)}
        color={color}
        sx={{ height: 8, borderRadius: 4 }}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.75}>
        <Box display="flex" gap={0.5}>
          <Chip size="small" label={`per ${period}`} variant="outlined" sx={{ textTransform: 'capitalize' }} />
          {isLow && (
            <Chip size="small" label={color === 'error' ? 'Low — close to limit' : 'Warning'} color={color} />
          )}
        </Box>
        {dashboard && (
          <Link
            href={dashboard}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
          >
            Provider dashboard <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        )}
      </Box>
    </Box>
  );
}

// ─── Source tags ──────────────────────────────────────────────────────────
const LiveTag = (
  <Chip
    size="small"
    icon={<CloudDoneIcon sx={{ fontSize: 14 }} />}
    label="Live"
    color="success"
    variant="outlined"
  />
);

const InternalTag = (
  <Chip
    size="small"
    icon={<StorageIcon sx={{ fontSize: 14 }} />}
    label="Internal counter"
    variant="outlined"
  />
);

// ─── Page ─────────────────────────────────────────────────────────────────
export default function Usage() {
  const [serpapi, setSerpapi] = useState(null);
  const [anthropic, setAnthropic] = useState(null);
  const [internal, setInternal] = useState(null);

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({ serpapi: null, anthropic: null, internal: null });
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    setLoading(true);
    const newErrors = { serpapi: null, anthropic: null, internal: null };

    // Fetch all three in parallel; each failure is captured independently so
    // one provider being down doesn't black out the whole page.
    const results = await Promise.allSettled([
      getSerpapiUsage(),
      getAnthropicUsage(),
      getApiUsage(),
    ]);

    if (results[0].status === 'fulfilled') {
      setSerpapi(results[0].value.data);
    } else {
      newErrors.serpapi = results[0].reason?.friendlyMessage || 'Could not fetch SerpAPI usage.';
      setSerpapi(null);
    }

    if (results[1].status === 'fulfilled') {
      setAnthropic(results[1].value.data);
    } else {
      newErrors.anthropic = results[1].reason?.friendlyMessage || 'Could not fetch Anthropic usage.';
      setAnthropic(null);
    }

    if (results[2].status === 'fulfilled') {
      setInternal(results[2].value.data);
    } else {
      newErrors.internal = results[2].reason?.friendlyMessage || 'Could not fetch internal counters.';
      setInternal(null);
    }

    setErrors(newErrors);
    setLastRefresh(new Date());
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Box p={3} maxWidth={800} mx="auto">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography variant="h5" fontWeight={600}>API Usage</Typography>
          <Typography variant="body2" color="text.secondary">
            Internal view. Not linked from the main nav.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={load}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
        >
          Refresh
        </Button>
      </Box>

      {lastRefresh && (
        <Typography variant="caption" color="text.disabled">
          Last refreshed {lastRefresh.toLocaleTimeString()}
        </Typography>
      )}

      {loading && !serpapi && !anthropic && !internal && (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      )}

      <Stack spacing={2} sx={{ mt: 2 }}>
        {/* ─── Live: SerpAPI ─────────────────────────────────────────── */}
        <Card>
          <CardContent>
            <Typography variant="overline" color="success.main" fontWeight={700}>
              Live data
            </Typography>
            {errors.serpapi && (
              <Alert severity="warning" sx={{ mt: 1 }}>{errors.serpapi}</Alert>
            )}
            {serpapi && (
              <UsageRow
                label="SerpAPI (Google Jobs)"
                description={serpapi.plan ? `Plan: ${serpapi.plan}` : 'Google Jobs search results'}
                used={serpapi.used}
                limit={serpapi.limit}
                remaining={serpapi.remaining}
                period={serpapi.period}
                resets={serpapi.resets}
                dashboard="https://serpapi.com/manage-api-key"
                sourceTag={LiveTag}
              />
            )}
          </CardContent>
        </Card>

        {/* ─── Live: Anthropic Admin API (optional) ──────────────────── */}
        <Card>
          <CardContent>
            <Typography variant="overline" color="success.main" fontWeight={700}>
              Live data
            </Typography>

            {errors.anthropic && (
              <Alert severity="warning" sx={{ mt: 1 }}>{errors.anthropic}</Alert>
            )}

            {anthropic?.source === 'not_configured' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Anthropic Admin API not configured
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Create an Admin API key at{' '}
                  <Link href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">
                    console.anthropic.com
                  </Link>{' '}
                  → Settings → Admin keys, then set <code>ANTHROPIC_ADMIN_API_KEY</code> in your
                  backend <code>.env</code>. Until then, use the internal counter below and the
                  Anthropic console directly.
                </Typography>
              </Alert>
            )}

            {anthropic?.source === 'live' && (
              <Box sx={{ py: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Anthropic (Claude) — month-to-date
                      </Typography>
                      {LiveTag}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Cost across all usage (Career Compass + any other project using this org).
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h5" fontWeight={700}>
                      ${anthropic.cost_usd?.toFixed(2) ?? '0.00'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      USD this month
                    </Typography>
                  </Box>
                </Box>
                <Link
                  href="https://console.anthropic.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  Open Anthropic Console <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ─── Internal counters ─────────────────────────────────────── */}
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              Internal counters
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Tracked by Career Compass locally in <code>data/api_usage.json</code>.
              Not authoritative — cross-check with each provider's dashboard.
            </Typography>

            {errors.internal && (
              <Alert severity="warning" sx={{ mt: 1 }}>{errors.internal}</Alert>
            )}

            {internal && INTERNAL_ORDER.map((api, idx) => {
              const data = internal[api];
              if (!data) return null;
              const meta = INTERNAL_META[api] || {};
              return (
                <Box key={api}>
                  <UsageRow
                    label={meta.label || api}
                    description={meta.description}
                    used={data.used}
                    limit={data.limit}
                    remaining={data.remaining}
                    period={data.period}
                    resets={data.resets}
                    dashboard={meta.dashboard}
                    sourceTag={InternalTag}
                  />
                  {idx < INTERNAL_ORDER.length - 1 && <Divider />}
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Stack>

      {/* Footer explainer */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          How to read this page
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          <strong>Live</strong> cards pull directly from the provider (source of truth).
          <strong> Internal counters</strong> are incremented by Career Compass each time
          we call an API — useful as a pre-flight gate to avoid wasted calls, but they
          can drift if a request fails after the counter bumps. Gmail is not tracked here
          because its free quota (1B units/day) is effectively unlimited.
        </Typography>
      </Alert>
    </Box>
  );
}
